import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Box, MenuItem, FormControl } from '@mui/material';
import { colors, IconButton, Tooltip, Typography } from '@sweep-io/sweep-design';
import { ArtifactRenderer } from './ArtifactRenderer';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { github } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { useArtifactPanelWidth } from '../../../hooks/useArtifactPanelWidth';
import { useResizeDrag } from '../../../hooks/useResizeDrag';
import { Close, ArrowRight, Share, ListBullets } from '@sweep-io/sweep-design/dist/icons';
import { useArtifacts } from './useArtifacts';
import SweepSelect, { SelectSizes } from '../SweepSelect';
import { useIsCanvasUnited } from '../../../hooks/useIsCanvasUnited';
import { useAiAgentApiFacade } from '../../../apis/facades/useAiAgentApiFacade';
import { getContentType } from '../../helpers/artifactHelpers';
import { CenteredCircularProgress } from '../CenteredCircularProgress';
import { ArtifactSelector } from './ArtifactSelector';
import { ShareArtifactPopover } from './ShareArtifactPopover';
import {
  getAllArtifactsFromChat,
  getArtifactParts,
  ArtifactSummary,
} from '../ai-chat/aiChatHelpers';
import { useSelector } from 'react-redux';
import { selectChatsDetails, selectActiveChatId } from '../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstanceOptional } from '../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../aiAgentCenter/ChatInstance';
import { telemetry } from '../../../telemetry';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';

export const ArtifactPanel: React.FC = () => {
  const {
    isArtifactPanelOpen,
    closeArtifactPanel,
    currentArtifactId,
    artifacts,
    streamingContent,
    artifactState,
    updateArtifactVersion,
    openExistingArtifact,
    addArtifactData,
  } = useArtifacts();

  const isCanvasUnited = useIsCanvasUnited();
  const { get_artifact } = useAiAgentApiFacade();
  const { artifactsPage: isNewSharedArtifactsEnabled } = useFeatureToggle();
  const [sharePopoverAnchorEl, setSharePopoverAnchorEl] = useState<HTMLElement | null>(null);

  const artifactData = currentArtifactId ? artifacts[currentArtifactId] : null;
  const { panelWidth, updatePanelWidth, minWidth, maxWidth } = useArtifactPanelWidth();
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  const streamingRef = useRef<HTMLDivElement>(null);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const [isArtifactSelectorOpen, setIsArtifactSelectorOpen] = useState(false);
  const [artifactSelectorAnchorEl, setArtifactSelectorAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  // Get chat messages to extract all artifacts
  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const chatsDetails = useSelector(selectChatsDetails);
  const currentChat = activeChatId && chatsDetails ? chatsDetails[activeChatId]?.data : null;

  // Extract all artifacts from chat and merge with loaded artifacts
  const allArtifacts = useMemo(() => {
    const chatArtifacts = currentChat?.aiChatDetails?.messages
      ? getAllArtifactsFromChat(currentChat.aiChatDetails.messages)
      : [];

    // Create a map of artifactId -> summary
    const artifactMap = new Map<string, ArtifactSummary>();

    // Add artifacts from chat history
    chatArtifacts.forEach((artifact) => {
      artifactMap.set(artifact.artifactId, artifact);
    });

    // Add artifacts from loaded context (in case chat history hasn't updated yet)
    Object.entries(artifacts).forEach(([artifactId, artifactData]) => {
      if (!artifactMap.has(artifactId)) {
        // This artifact is loaded but not yet in chat history (e.g., just streamed)
        artifactMap.set(artifactId, {
          artifactId,
          title: artifactData.versions[0]?.title || 'Untitled Artifact',
          versionCount: artifactData.versions.length,
        });
      }
    });

    return Array.from(artifactMap.values());
  }, [currentChat, artifacts]);

  const {
    isResizing,
    isHovering: isHoveringHandle,
    handleResizeStart,
    handleMouseEnter,
    handleMouseLeave,
  } = useResizeDrag({
    onResize: updatePanelWidth,
    minValue: minWidth,
    maxValue: maxWidth,
    calculateDiff: (e: MouseEvent, startX: number) => {
      return startX - e.clientX;
    },
  });

  useEffect(() => {
    if (artifactData && artifactData.versions.length > 0) {
      const currentVersion =
        artifactData.currentVersion ||
        artifactData.versions[artifactData.versions.length - 1].versionId;
      setSelectedVersion(currentVersion);
    }
  }, [artifactData, artifactData?.currentVersion]);

  useEffect(() => {
    if (artifactState === 'streaming' && streamingRef.current && !isUserScrolledUp) {
      const element = streamingRef.current;
      element.scrollTop = element.scrollHeight;
    }
  }, [streamingContent, artifactState, isUserScrolledUp]);

  const handleScroll = useCallback(() => {
    if (streamingRef.current) {
      const element = streamingRef.current;
      const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
      setIsUserScrolledUp(!isAtBottom);
    }
  }, []);

  useEffect(() => {
    if (artifactState === 'streaming') {
      setIsUserScrolledUp(false);
    }
  }, [artifactState]);

  useEffect(() => {
    if (artifactState === 'compiling' && streamingRef.current) {
      streamingRef.current.scrollTop = streamingRef.current.scrollHeight;
    }
  }, [artifactState]);

  // Clean streaming content to remove code fence markers during display
  const cleanedStreamingContent = useMemo(() => {
    if (!streamingContent) return '';

    return streamingContent
      .replace(/^```[a-z]*\n/i, '') // Remove opening fence (e.g., ```jsx)
      .replace(/\n```\s*$/i, ''); // Remove closing fence (if it arrived)
  }, [streamingContent]);

  // Fetch version content if needed when selectedVersion changes
  useEffect(() => {
    const fetchVersionIfNeeded = async () => {
      if (!artifactData || !currentArtifactId || !selectedVersion) return;

      const version = artifactData.versions.find((v) => v.versionId === selectedVersion);
      if (!version) return;

      // Check if version needs to be fetched (content is empty)
      if (!version.content && version.versionId) {
        setIsLoadingVersion(true);
        try {
          const fetchedArtifact = await get_artifact(version.versionId);
          const detectedContentType = getContentType(fetchedArtifact.code);

          // Update the version with fetched data
          updateArtifactVersion(currentArtifactId, version.versionId, {
            content: fetchedArtifact.code,
            contentType: detectedContentType,
            createdAt: fetchedArtifact.createdAt,
            version: fetchedArtifact.version,
          });
        } catch (error) {
          console.error('Failed to fetch artifact version:', error);
        } finally {
          setIsLoadingVersion(false);
        }
      }
    };

    fetchVersionIfNeeded();
  }, [selectedVersion, artifactData, currentArtifactId, get_artifact, updateArtifactVersion]);

  const handleVersionChange = useCallback((versionId: string) => {
    // Just update the selected version, the useEffect above will handle fetching if needed
    setSelectedVersion(versionId);
  }, []);

  const currentVersion = useMemo(() => {
    if (!artifactData || !selectedVersion) return null;
    return artifactData.versions.find((v) => v.versionId === selectedVersion);
  }, [artifactData, selectedVersion]);

  const handleShareArtifact = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!currentVersion?.versionId) return;

      if (isNewSharedArtifactsEnabled) {
        // New behavior: Open share popover
        setSharePopoverAnchorEl(event.currentTarget);
      } else {
        // Legacy behavior: Open shared artifact page in new tab
        const shareUrl = `/share-artifacts/${currentVersion.versionId}`;
        window.open(shareUrl, '_blank');
      }
    },
    [currentVersion, isNewSharedArtifactsEnabled],
  );

  const handleCloseSharePopover = useCallback(() => {
    setSharePopoverAnchorEl(null);
  }, []);

  const handleOpenArtifactSelector = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setArtifactSelectorAnchorEl(event.currentTarget);
    setIsArtifactSelectorOpen(true);
  }, []);

  const handleCloseArtifactSelector = useCallback(() => {
    setIsArtifactSelectorOpen(false);
    setArtifactSelectorAnchorEl(null);
  }, []);

  const handleSelectArtifact = useCallback(
    async (artifactId: string) => {
      handleCloseArtifactSelector();

      if (artifactId === currentArtifactId) {
        return;
      }

      // Get the selected artifact from loaded artifacts
      const selectedArtifact = artifacts[artifactId];
      if (selectedArtifact && selectedArtifact.versions.length > 0) {
        const latestVersion =
          selectedArtifact.versions[selectedArtifact.versions.length - 1].versionId;
        openExistingArtifact(artifactId, latestVersion);
      } else {
        // Artifact not loaded yet - find its versionId from chat messages and load it
        if (currentChat?.aiChatDetails?.messages) {
          const allVersionRefs: Array<{ versionId: string; title: string }> = [];

          currentChat.aiChatDetails.messages.forEach((message) => {
            const artifactParts = getArtifactParts(message.content);
            artifactParts.forEach((part) => {
              if (part.artifactId === artifactId) {
                allVersionRefs.push({
                  versionId: part.versionId,
                  title: part.title,
                });
              }
            });
          });

          if (allVersionRefs.length > 0) {
            try {
              // Get the latest version from chat
              const latestVersionRef = allVersionRefs[allVersionRefs.length - 1];
              const fetchedArtifact = await get_artifact(latestVersionRef.versionId);

              // Detect content type from the code
              const detectedContentType = getContentType(fetchedArtifact.code);

              // Create versions array with fetched data for latest version,
              // and placeholders for other versions (to be lazy loaded) for the version dropdown
              const versions = allVersionRefs.map((ref) => {
                if (ref.versionId === fetchedArtifact.id) {
                  // This is the version we just fetched - include full data
                  return {
                    versionId: fetchedArtifact.id,
                    title: ref.title.replace(/^"|"$/g, ''),
                    content: fetchedArtifact.code,
                    contentType: detectedContentType,
                    createdAt: fetchedArtifact.createdAt,
                    version: fetchedArtifact.version,
                  };
                } else {
                  // Placeholder for other versions - will be lazy loaded when selected
                  return {
                    versionId: ref.versionId,
                    title: ref.title.replace(/^"|"$/g, ''),
                    content: '',
                    contentType: detectedContentType,
                    createdAt: '',
                    version: undefined,
                  };
                }
              });

              // Sort versions by the order they appear in chat (earlier = lower version)
              const sortedVersions = versions.map((v, index) => ({
                ...v,
                version: v.version ?? index + 1, // Assign version numbers based on order
              }));

              // Add to artifacts store
              const artifactDataToStore = {
                artifactId: artifactId,
                versions: sortedVersions,
                currentVersion: fetchedArtifact.id,
              };

              addArtifactData(artifactId, artifactDataToStore);
              openExistingArtifact(artifactId, fetchedArtifact.id);
            } catch (error) {
              telemetry.captureError(error);
            }
          }
        }
      }
    },
    [
      artifacts,
      openExistingArtifact,
      currentArtifactId,
      currentChat,
      handleCloseArtifactSelector,
      get_artifact,
      addArtifactData,
    ],
  );

  // Show ListBullets button only when there are 2+ artifacts
  const shouldShowArtifactSelector = allArtifacts.length >= 2;

  if (!isArtifactPanelOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        width: `${panelWidth}px`,
        height: '100%',
        backgroundColor: 'white',
        borderLeft: `1px solid ${colors.grey[200]}`,
        ...(isCanvasUnited && {
          borderTop: `1px solid ${colors.grey[200]}`,
          borderRadius: '8px 0 0 0',
          marginTop: '48px',
          height: 'calc(100% - 48px)',
        }),
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Drag overlay - prevents iframe from stealing mouse events during resize */}
      {isResizing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 500, // Above everything including iframe
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            pointerEvents: 'all', // Capture all mouse events during drag
          }}
        />
      )}
      {/* Resize Handle - Larger hit area with visible indicator */}
      <Box
        className="artifact-panel-resize-handle"
        sx={{
          position: 'absolute',
          left: '-2px', // Extend slightly outside the panel
          top: 0,
          bottom: 0,
          width: '6px', // Larger hit area
          cursor: 'col-resize',
          zIndex: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover .resize-indicator': {
            opacity: 1,
            backgroundColor: colors.blue[500],
          },
        }}
        onMouseDown={(e) => handleResizeStart(e, panelWidth)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Visual indicator - the actual blue line */}
        <Box
          className="resize-indicator"
          sx={{
            width: '2px',
            height: '100%',
            transition: 'opacity 0.2s ease-in-out, background-color 0.2s ease-in-out',
            backgroundColor: isHoveringHandle || isResizing ? colors.blue[500] : 'transparent',
            opacity: isHoveringHandle || isResizing ? 1 : 0,
          }}
        />
      </Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isCanvasUnited ? '8px 12px 8px 16px' : '8px 16px',
          borderBottom: artifactState === 'streaming' ? 'none' : `1px solid ${colors.grey[200]}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {shouldShowArtifactSelector && (
            <Box
              sx={{
                position: 'relative',
                zIndex: isArtifactSelectorOpen ? 700 : 'auto',
                '& button': {
                  backgroundColor: isArtifactSelectorOpen ? colors.grey[120] : undefined,
                },
              }}
            >
              <Tooltip title="View all artifacts" placement="top">
                <IconButton variant="flat" size="small" onClick={handleOpenArtifactSelector}>
                  <ListBullets />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {/* TODO: Temporarily hidden - re-enable when versions are ready */}
          {artifactData && artifactData.versions.length > 0 && false && (
            <FormControl size="small">
              <SweepSelect
                variant={SelectSizes.medium}
                SelectProps={{
                  value: selectedVersion ?? '',
                  onChange: (e) => handleVersionChange(e.target.value as string),
                }}
              >
                {artifactData?.versions.map((version) => (
                  <MenuItem key={version.versionId} value={version.versionId}>
                    V{version.version ?? '1'}
                  </MenuItem>
                ))}
              </SweepSelect>
            </FormControl>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Share artifact" placement="top">
            <IconButton variant="flat" size="small" onClick={handleShareArtifact}>
              <Share />
            </IconButton>
          </Tooltip>
          <IconButton onClick={closeArtifactPanel} size="small" variant="flat">
            {isCanvasUnited ? <ArrowRight size="medium" /> : <Close size="medium" />}
          </IconButton>
        </Box>
      </Box>

      {/* Backdrop for artifact selector - only blurs the artifact panel */}
      {isArtifactSelectorOpen && (
        <Box
          onClick={handleCloseArtifactSelector}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 600,
            backdropFilter: 'blur(8px)',
          }}
        />
      )}

      <ArtifactSelector
        artifacts={allArtifacts}
        currentArtifactId={currentArtifactId}
        isOpen={isArtifactSelectorOpen}
        anchorEl={artifactSelectorAnchorEl}
        onClose={handleCloseArtifactSelector}
        onSelectArtifact={handleSelectArtifact}
      />

      {/* Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        {artifactState === 'streaming' && (
          <Box
            ref={streamingRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: 'auto',
              backgroundColor: 'white',
              maxHeight: '100%',
            }}
          >
            {!streamingContent && (
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: colors.grey[50],
                }}
              >
                <Typography variant="body" color={colors.grey[700]}>
                  Generating code...
                </Typography>
              </Box>
            )}
            <Box sx={{ position: 'relative', backgroundColor: 'white' }}>
              <SyntaxHighlighter
                language="javascript"
                style={github}
                customStyle={{
                  margin: 0,
                  padding: '16px',
                  backgroundColor: 'white',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  border: 'none',
                }}
                showLineNumbers={false}
                wrapLines={true}
              >
                {cleanedStreamingContent || '// Code will appear here...'}
              </SyntaxHighlighter>
            </Box>
          </Box>
        )}

        {artifactState === 'compiling' && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <CenteredCircularProgress />
          </Box>
        )}

        {artifactState === 'rendered' && currentVersion && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {isLoadingVersion || !currentVersion.content ? (
              <Box
                sx={{
                  mt: '300px',
                  textAlign: 'center',
                }}
              >
                <CenteredCircularProgress />
                <Typography variant="caption" color={colors.grey[800]}>
                  Loading
                </Typography>
              </Box>
            ) : (
              <ArtifactRenderer
                content={currentVersion.content}
                contentType={currentVersion.contentType}
              />
            )}
          </Box>
        )}

        {artifactState === 'error' && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              color: colors.blush[600],
              maxWidth: '80%',
            }}
          >
            <Typography variant="h6">Error</Typography>
            <Typography variant="body" textAlign="center">
              Failed to compile or render the artifact. Please try again.
            </Typography>
          </Box>
        )}
      </Box>

      {isNewSharedArtifactsEnabled && currentVersion && (
        <ShareArtifactPopover
          anchorEl={sharePopoverAnchorEl}
          onClose={handleCloseSharePopover}
          artifact={{
            id: currentVersion.versionId,
            title: currentVersion.title,
          }}
        />
      )}
    </Box>
  );
};
